/**
 * ImageUpload — domain component for uploading a single image asset.
 *
 * Replaces the legacy `<image-upload>` Custom Element. Implements the same
 * three-step upload protocol (presigned URL → direct PUT to MinIO → callback
 * with the public URL), but in a controlled React shell that integrates with
 * react-hook-form and the surrounding design system.
 *
 * Visual states (mirrors `static/admin/js/components/image-upload.js`):
 *   - Empty: dashed dropzone with placeholder + size hint
 *   - Preview: image with "Trocar" / "Remover" actions
 *   - Uploading: spinner with "Enviando..." text
 *   - Error: inline message + retry CTA
 *
 * The component is *controlled*: `value` is the source of truth for the
 * preview, and `onChange` reports the new public URL after a successful
 * upload (or `null` when the user removes the image). Failed uploads keep
 * the previous `value` intact (EC06).
 *
 * Colors: the dropzone/preview/uploading states use `--upload-*` CSS
 * variables (defined here via Tailwind arbitrary values with a fallback to
 * the admin dark-theme palette) instead of the `bg-bg-card`/`border-border`/
 * `text-primary` utilities directly. `.merchant-theme` (see
 * `styles/merchant-theme.css`) overrides those variables to light
 * equivalents, so this component renders correctly both in admin's dark
 * panel (falls back — unchanged) and staff/comerciante's light panel
 * (picks up the override) without a second implementation.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactElement,
} from 'react';
import { CheckCircle2, Image as ImageIcon, Trash2, Upload } from 'lucide-react';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { api } from '../../api/client';
import { ApiError } from '../../api/errors';
import { toast } from '../../lib/toast';
import { cn } from '../../lib/cn';

/** Default maximum file size when not provided by the consumer. */
const DEFAULT_MAX_SIZE_MB = 5;
const BYTES_PER_MB = 1024 * 1024;

/** MIME types accepted by the backend upload policies for image assets. */
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png'] as const;
const ACCEPT_ATTR = ALLOWED_MIME_TYPES.join(',');

/**
 * Server-side upload purposes by legacy bucket name. The backend no longer
 * accepts a client-chosen bucket — it derives bucket/prefix/limits from the
 * purpose (AUD-004).
 */
const PURPOSE_BY_BUCKET: Record<string, string> = {
  'users-documents': 'USER_DOCUMENT',
  'product-images': 'PRODUCT_IMAGE',
  'banner-images': 'BANNER_IMAGE',
  'category-icons': 'CATEGORY_ICON',
  'notification-images': 'NOTIFICATION_IMAGE',
};

/**
 * Buckets whose objects are PRIVATE (LGPD) — the stored URL 403s when loaded
 * directly in an `<img>`. For these, the preview must come from the locally
 * selected `File` (object URL); a restored/remote-only value renders a
 * neutral "sent" state instead of a broken image.
 * ponytail: single private bucket today; extend this set if more appear.
 */
const PRIVATE_BUCKETS = new Set<string>(['users-documents']);

export interface ImageUploadProps {
  /** Target MinIO bucket (e.g. `category-icons`, `banner-images`). */
  bucket: string;
  /** Current image URL (controlled). `null`/`undefined` means empty. */
  value?: string | null;
  /**
   * Called after a successful upload with the new public URL, or with
   * `null` when the user clicks "Remover".
   */
  onChange: (url: string | null) => void;
  /**
   * Optional error callback. Defaults to firing a `toast.error` so consumers
   * don't need to wire one explicitly.
   */
  onError?: (error: Error) => void;
  /** Maximum file size in MB. Defaults to 5. */
  maxSizeMB?: number;
  /** Visual aspect-ratio hint (e.g. `1:1`, `16:9`). Does not block uploads. */
  aspectRatio?: string;
  /** Override the placeholder copy. */
  placeholder?: string;
  /** Disable interactions while keeping the preview visible. */
  disabled?: boolean;
  /** Extra classes for the outer wrapper. */
  className?: string;
}

/** Imperative handle exposed via `ref` — limited to actions, not internals. */
export interface ImageUploadHandle {
  /** Programmatically open the native file picker. */
  openPicker: () => void;
}

/**
 * Shape of `POST /api/v1/storage/upload-url`.
 *
 * The generated OpenAPI schema lags the backend DTOs, so we model the
 * runtime contract here and cast the openapi-fetch response — see
 * `requestPresignedUrl`.
 */
interface PresignedUploadResponse {
  uploadUrl: string;
  objectKey: string;
  publicUrl?: string | null;
  requiredHeaders?: Record<string, string>;
}

/** Shape of `POST /api/v1/storage/publish`. */
interface PublishedUploadResponse {
  objectKey: string;
  objectUrl: string;
  publicUrl?: string | null;
}

/** Response wrapper from openapi-fetch; we read `data` if present. */
interface OpenApiFetchResult<T> {
  data?: T;
  error?: unknown;
}

/**
 * Validates a file against client-side rules. Throws a user-facing message
 * (PT-BR) when invalid. Pure — easily unit-testable.
 */
function validateFile(file: File, maxSizeMB: number): void {
  const allowed: readonly string[] = ALLOWED_MIME_TYPES;
  if (!allowed.includes(file.type)) {
    throw new Error(`Tipo de arquivo invalido. Aceitos: ${ALLOWED_MIME_TYPES.join(', ')}`);
  }
  const maxBytes = maxSizeMB * BYTES_PER_MB;
  if (file.size > maxBytes) {
    const sizeMb = (file.size / BYTES_PER_MB).toFixed(1);
    throw new Error(`Arquivo muito grande (${sizeMb} MB). Maximo permitido: ${maxSizeMB} MB.`);
  }
}

/**
 * Requests a presigned PUT URL from the backend. Wraps openapi-fetch and
 * casts the response to the runtime shape (see `PresignedUploadResponse`).
 */
function purposeForBucket(bucket: string): string {
  const purpose = PURPOSE_BY_BUCKET[bucket];
  if (!purpose) {
    throw new Error('Destino de upload nao permitido.');
  }
  return purpose;
}

async function requestPresignedUrl(
  purpose: string,
  file: File,
): Promise<PresignedUploadResponse> {
  const result = (await api.POST('/api/v1/storage/upload-url', {
    body: {
      purpose,
      fileName: file.name,
      contentType: file.type,
      contentLength: file.size,
    } as never,
  })) as OpenApiFetchResult<PresignedUploadResponse>;

  const data = result.data;
  if (!data || !data.uploadUrl || !data.objectKey) {
    throw new Error('Resposta invalida do servidor de upload.');
  }
  return data;
}

/**
 * Confirms the staged upload so the backend can validate the object and move
 * it to its published (readable) key.
 */
async function publishUpload(
  purpose: string,
  objectKey: string,
): Promise<PublishedUploadResponse> {
  const path =
    `/api/v1/storage/publish?purpose=${encodeURIComponent(purpose)}` +
    `&objectKey=${encodeURIComponent(objectKey)}`;
  const result = (await api.POST(path as never)) as OpenApiFetchResult<PublishedUploadResponse>;
  const data = result.data;
  if (!data || !data.objectUrl) {
    throw new Error('Resposta invalida do servidor de upload.');
  }
  return data;
}

/**
 * Performs the direct PUT to MinIO using the presigned URL. We bypass the
 * api-client middleware here because:
 *   1) the URL is on a different origin (MinIO) and must not carry our
 *      `Authorization: Bearer` header,
 *   2) we send the raw file as the body — `openapi-fetch` is built around
 *      JSON request/response.
 */
async function uploadToMinio(
  uploadUrl: string,
  file: File,
  requiredHeaders?: Record<string, string>,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: requiredHeaders ?? { 'Content-Type': file.type },
  });
  if (!res.ok) {
    throw new Error(`Falha ao enviar imagem (HTTP ${res.status})`);
  }
}

/** Convert any thrown value to a user-facing `Error` instance. */
function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (value instanceof ApiError) return value;
  return new Error('Erro desconhecido ao enviar imagem.');
}

/** Best-effort file name for the "Documento enviado" label; empty string if unparseable. */
function fileNameFromUrl(url: string): string {
  try {
    const path = new URL(url).pathname;
    return decodeURIComponent(path.split('/').pop() ?? '');
  } catch {
    return '';
  }
}

/** Pull the first file out of a drop event, or null if none. */
function pickDroppedFile(event: DragEvent<HTMLDivElement>): File | null {
  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) return null;
  return files[0] ?? null;
}

/**
 * ImageUpload component (forwardRef so parents can trigger the picker).
 */
export const ImageUpload = forwardRef<ImageUploadHandle, ImageUploadProps>(function ImageUpload(
  {
    bucket,
    value,
    onChange,
    onError,
    maxSizeMB = DEFAULT_MAX_SIZE_MB,
    aspectRatio,
    placeholder = 'Arraste a imagem aqui ou clique para selecionar',
    disabled = false,
    className,
  },
  ref,
): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldId = useId();
  const [isUploading, setUploading] = useState(false);
  const [isDragOver, setDragOver] = useState(false);
  // Locally selected file, kept around purely so the preview can read the pixels
  // straight off disk (`URL.createObjectURL`) instead of re-fetching the (possibly
  // private) remote URL — see PRIVATE_BUCKETS above.
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const isPrivateBucket = PRIVATE_BUCKETS.has(bucket);

  useImperativeHandle(ref, () => ({
    openPicker: () => inputRef.current?.click(),
  }));

  // Derive the object URL from the selected file and revoke it on change/unmount.
  useEffect(() => {
    if (!selectedFile) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const reportError = useCallback(
    (error: Error): void => {
      if (onError) {
        onError(error);
      } else {
        toast.error(error.message);
      }
    },
    [onError],
  );

  const handleFile = useCallback(
    async (file: File): Promise<void> => {
      try {
        validateFile(file, maxSizeMB);
      } catch (e) {
        reportError(toError(e));
        return;
      }
      setSelectedFile(file);
      setUploading(true);
      try {
        const purpose = purposeForBucket(bucket);
        const presigned = await requestPresignedUrl(purpose, file);
        await uploadToMinio(presigned.uploadUrl, file, presigned.requiredHeaders);
        const published = await publishUpload(purpose, presigned.objectKey);
        onChange(published.publicUrl ?? published.objectUrl);
      } catch (e) {
        reportError(toError(e));
        setSelectedFile(null); // EC06: failed upload keeps the previous value's preview.
      } finally {
        setUploading(false);
      }
    },
    [bucket, maxSizeMB, onChange, reportError],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (file) {
        void handleFile(file);
      }
    },
    [handleFile],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setDragOver(false);
      if (disabled || isUploading) return;
      const file = pickDroppedFile(e);
      if (file) void handleFile(file);
    },
    [disabled, handleFile, isUploading],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((): void => setDragOver(false), []);

  const openPicker = useCallback((): void => {
    if (disabled || isUploading) return;
    inputRef.current?.click();
  }, [disabled, isUploading]);

  const handleRemove = useCallback((): void => {
    if (disabled || isUploading) return;
    setSelectedFile(null);
    onChange(null);
  }, [disabled, isUploading, onChange]);

  const aspectStyle = aspectRatio
    ? { aspectRatio: aspectRatio.replace(':', ' / ') }
    : undefined;
  const hasValue = Boolean(value);
  const showPreview = hasValue && !isUploading;
  const showDropzone = !hasValue && !isUploading;

  return (
    <div className={cn('w-full', className)}>
      <input
        ref={inputRef}
        id={fieldId}
        type="file"
        accept={ACCEPT_ATTR}
        className="sr-only"
        aria-label="Selecionar imagem"
        onChange={handleInputChange}
        disabled={disabled || isUploading}
      />

      <div
        className={cn(
          'relative w-full overflow-hidden rounded-card border transition-colors',
          'bg-[var(--upload-bg,rgba(30,41,59,.4))]',
          showDropzone
            ? 'border-dashed border-[var(--upload-border,#334155)]'
            : 'border-solid border-[var(--upload-border,#334155)]',
          isDragOver &&
            'border-[var(--upload-accent,#f97316)] bg-[var(--upload-accent-tint,rgba(249,115,22,.05))]',
          disabled && 'opacity-60',
        )}
        style={aspectStyle}
        onDragOver={showDropzone ? handleDragOver : undefined}
        onDragLeave={showDropzone ? handleDragLeave : undefined}
        onDrop={showDropzone ? handleDrop : undefined}
      >
        {isUploading ? <UploadingState /> : null}
        {showPreview ? (
          objectUrl ? (
            <PreviewImage src={objectUrl} />
          ) : isPrivateBucket ? (
            <SentPrivateState url={value ?? ''} />
          ) : (
            <PreviewImage src={value ?? ''} />
          )
        ) : null}
        {showDropzone ? (
          <DropzonePrompt
            placeholder={placeholder}
            maxSizeMB={maxSizeMB}
            onClick={openPicker}
            disabled={disabled}
          />
        ) : null}
      </div>

      {showPreview ? (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leftIcon={<Upload size={16} aria-hidden />}
            onClick={openPicker}
            disabled={disabled || isUploading}
          >
            Trocar
          </Button>
          {/* FAV-170: discoverable destructive action — was `ghost` (too faint). */}
          <Button
            type="button"
            variant="danger"
            size="sm"
            leftIcon={<Trash2 size={16} aria-hidden />}
            onClick={handleRemove}
            disabled={disabled || isUploading}
            aria-label="Remover imagem"
          >
            Remover
          </Button>
        </div>
      ) : null}
    </div>
  );
});

/* ------------------------------------------------------------------ */
/* Internal subcomponents                                              */
/* ------------------------------------------------------------------ */

function UploadingState(): ReactElement {
  return (
    <div className="flex h-full min-h-[160px] w-full flex-col items-center justify-center gap-3 p-6 text-[var(--upload-fg-muted,#94a3b8)]">
      <Spinner size="lg" className="text-[var(--upload-accent,#f97316)]" />
      <p className="text-sm">Enviando...</p>
    </div>
  );
}

interface PreviewImageProps {
  src: string;
}

function PreviewImage({ src }: PreviewImageProps): ReactElement {
  return (
    <img
      src={src}
      alt="Pré-visualização da imagem enviada"
      className="block h-full w-full object-contain"
    />
  );
}

interface SentPrivateStateProps {
  url: string;
}

/**
 * Neutral confirmation shown for a restored (no local File) value in a
 * PRIVATE bucket — the object 403s if we try to load it directly, so we
 * don't attempt an `<img>` at all.
 */
function SentPrivateState({ url }: SentPrivateStateProps): ReactElement {
  const fileName = fileNameFromUrl(url);
  return (
    <div className="flex h-full min-h-[160px] w-full flex-col items-center justify-center gap-2 p-6 text-center text-[var(--upload-fg-muted,#94a3b8)]">
      <CheckCircle2 size={32} className="text-[var(--upload-accent,#f97316)]" aria-hidden />
      <p className="text-sm font-medium text-[var(--upload-fg,#ffffff)]">Documento enviado</p>
      {fileName ? <p className="max-w-full truncate text-xs">{fileName}</p> : null}
    </div>
  );
}

interface DropzonePromptProps {
  placeholder: string;
  maxSizeMB: number;
  onClick: () => void;
  disabled: boolean;
}

function DropzonePrompt({
  placeholder,
  maxSizeMB,
  onClick,
  disabled,
}: DropzonePromptProps): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex h-full min-h-[160px] w-full flex-col items-center justify-center gap-2 p-6 text-center',
        'text-[var(--upload-fg-muted,#94a3b8)] hover:text-[var(--upload-fg-hover,#ffffff)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--upload-accent,#f97316)]',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--upload-ring-offset,#1e293b)]',
      )}
    >
      <ImageIcon size={32} aria-hidden />
      <p className="text-sm font-medium">{placeholder}</p>
      <p className="text-xs">JPG ou PNG, max {maxSizeMB} MB</p>
    </button>
  );
}
